import { createRouter, createWebHistory } from 'vue-router';

import NotFound from '@lib/pages/NotFound.vue';
import lib_routes from '@lib/router';
import { extensionRoutes } from '@lib/extensions';

const router = createRouter({
	history: createWebHistory(),
	routes: [
		/*
		 * The printable letter, deliberately outside the dashboard layout. It is opened in a
		 * tab of its own and the only thing on it is the sheet - a navbar and a sidebar
		 * around a Briefpapier read as part of the page and print as nothing at all. The
		 * page asks the API for the sheet of the firm it is writing to, so it needs nothing
		 * from the layout it is not in.
		 */
		{
			path: '/law-firms/:id/communications/:communication_id/print',
			name: 'CommunicationPrint',
			component: () => import('@/pages/LawFirmDetails/CommunicationPrint.vue'),
			meta: { auth: true },
		},
		/*
		 * The heir chart for the Nachlassgericht, outside the layout for the same reason: it is
		 * a document, and a sidebar around it prints as nothing at all. A4 landscape, the chart
		 * on the first sheet and the register of parties on the second.
		 */
		{
			path: '/matters/:id/heir-chart/print',
			name: 'MatterHeirChartPrint',
			component: () => import('@/pages/MatterDetails/HeirChartPrint.vue'),
			meta: { auth: true },
		},
		{
			path: '/',
			component: () => import('@lib/layouts/Dashboard/Main.vue'),
			meta: { auth: true },
			children: [
				{ path: '', redirect: { name: 'Home' } },

				{ path: 'dashboard', name: 'Home', component: () => import('@/pages/Dashboard.vue') },

				// Law firms - the onboarding side
				{ path: 'pipeline', name: 'Pipeline', component: () => import('@/pages/LawFirms/Pipeline.vue') },
				{ path: 'law-firms', name: 'LawFirms', component: () => import('@/pages/LawFirms/LawFirms.vue') },
				{
					path: 'law-firms/:id',
					name: 'LawFirmDetails',
					component: () => import('@/pages/LawFirmDetails/LawFirmDetails.vue'),
				},

				// Cases - the work side
				{ path: 'matters', name: 'Matters', component: () => import('@/pages/Matters/Matters.vue') },
				{
					path: 'matters/:id',
					name: 'MatterDetails',
					component: () => import('@/pages/MatterDetails/MatterDetails.vue'),
				},

				// Across both sides: assigned work, and everything that carries a date
				{ path: 'tasks', name: 'Tasks', component: () => import('@/pages/Tasks/Tasks.vue') },
				// The shared notebook. The per record ones are tabs of the Kanzlei and the
				// Fall; this one belongs to nothing in particular, which is why it is a page.
				{ path: 'notes', name: 'Notes', component: () => import('@/pages/Notes.vue') },
				{ path: 'calendar', name: 'Calendar', component: () => import('@/pages/Calendar/Calendar.vue') },

				// Admin pages, mostly straight from the lib. There is no teams page: the app
				// dropped the context, and the offices it stood for are the Standorte.
				{ path: 'admin/users', component: () => import('@lib/pages/Admin/Users.vue') },
				{ path: 'admin/locations', name: 'Locations', component: () => import('@/pages/Admin/Locations.vue') },
				{
					path: 'admin/correspondence-templates',
					name: 'CorrespondenceTemplates',
					component: () => import('@/pages/Admin/CorrespondenceTemplates.vue'),
				},
				{
					path: 'admin/tags',
					name: 'Tags',
					component: () => import('@/pages/Admin/Tags.vue'),
				},
				// The lib's settings screen; its design tab shows proERBE's editor, see app.ts
				{ path: 'admin/settings', component: () => import('@lib/pages/Admin/Settings/Settings.vue') },
				// The Briefpapier is a tab of the settings now, beside the Mail-Design; an old link lands there.
				{ path: 'admin/letterhead', redirect: { path: '/admin/settings', query: { tab: 'letterhead' } } },
				{ path: 'admin/maintenance', component: () => import('@lib/pages/Maintenance/Maintenance.vue') },
				{ path: 'admin/development', component: () => import('@lib/pages/Development/Development.vue') },

				{ path: 'me', name: 'Profile', component: () => import('@lib/pages/Me/Me.vue') },

				// The pages of the lib's extensions that are on, today the MCP server's info page
				...extensionRoutes(),

				{ path: ':pathMatch(.*)*', name: '404', component: NotFound },
			],
		},
		...lib_routes,
	],
});

export default router;
